// deps
import React, { memo, useCallback } from "react";

// internal-deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { isAndroidPlatform, isiOSPlatform, CommonHeader, useEffectOnce } from "@swiggy-private/core-ui";
import {
    closeWebview,
    DineoutCheckoutRoute,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    useCustomHardwareBack,
} from "@swiggy-private/checkout-core";

// interfaces
import { IRestaurantDetails } from "@dineout-cart-client/interfaces";

// styles
import { HeaderContainer, DineoutHeader } from "./styles";
import { isEmpty } from "@swiggy-private/js-utils";

const Header = ({
    restaurantDetails,
    isEventsBookingFlow,
}: {
    restaurantDetails?: IRestaurantDetails;
    isEventsBookingFlow: boolean;
}) => {
    const _onBack = useCallback(() => {
        const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
        const isAppPlatform = isiOSPlatform(platform) || isAndroidPlatform(platform);

        if (isAppPlatform) {
            closeWebview();
            return;
        }

        /*
         * This component requires a custom back handling for the special usecase through which it is rendered.
         * We are using redirection logic to load the DO cart on web, and the usual browser provided convention
         * of using histoy.back() does not work here. This is because of the custom MiniRouter than CW uses,
         * it pushes a state manually once it is loaded, so during redirection from caller web-client e.g, SEO,
         * browser pushes state first to the history stack and CW also pushes a state in history stack,
         * which causes problem with back handling. To revert its effect, we are doing history.go(-2) to undo
         * 2 states instead of just 1 through history.back()
         */
        history.go(-2);
    }, []);

    useCustomHardwareBack([DineoutCheckoutRoute.TABLE_RESERVATION_CART], _onBack);

    const { name: restaurantName = "", city: restaurantCity = "" } = restaurantDetails || {};

    useEffectOnce(() => {
        if (!isEventsBookingFlow) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CART_P0_COMPONENT_IMPRESSION,
                componentName: "table_reservation_cart_header",
                isRestaurantNameVisible: String(!isEmpty(restaurantName)),
            });
        }
    });

    return (
        <DineoutHeader>
            <CommonHeader onBack={_onBack} isVisible={true} dataTestId="table-reservation-cart-header">
                <HeaderContainer>
                    <Text variant="V2.body1.neutral.bold" color="text_High_Emphasis">
                        {restaurantName}
                    </Text>
                    <Text variant="V2.body1.neutral.regular" color="text_Low_Emphasis">
                        {restaurantCity}
                    </Text>
                </HeaderContainer>
            </CommonHeader>
        </DineoutHeader>
    );
};
export default memo(Header);
